import { faClose } from '@fortawesome/free-solid-svg-icons'
import { FullScreenModal } from 'V2Components'
import { getHTMLDataTestID } from 'infra/utils'
import {
  Button,
  Label,
  LabelCheckbox,
  LoadingSpinner,
  SwitchBox,
  TargetIcon,
  TextInput,
  Typography
} from 'procyon-ui'
import React, { useState, useRef } from 'react'

const Jira = ({
  showIntegrationModal,
  setShowIntegrationModal,
  onInputChange,
  onContinue,
  isDisabled,
  isSelfHosted,
  setIsSelfHosted,
  message,
  nameError
}) => {
  const [tagInput, setTagInput] = useState('')
  const [tags, setTags] = useState([])
  const [fetchAll, setFetchAll] = useState(true)

  const addTag = (value) => {
    const trimmed = value.trim()
    if (!trimmed) return
    const upper = trimmed.toUpperCase()
    if (!tags.includes(upper)) setTags((prev) => [...prev, upper])
  }

  const handleTagKeyDown = (e) => {
    if (e.key === ',' || e.key === 'Enter') {
      e.preventDefault()
      addTag(tagInput)
      setTagInput('')
    } else if (e.key === 'Backspace' && tagInput === '') {
      setTags((prev) => prev.slice(0, prev.length - 1))
    }
  }

  const handleTagBlur = () => {
    if (tagInput) {
      addTag(tagInput)
      setTagInput('')
    }
  }

  const handleTagDelete = (index) => {
    setTags((prev) => prev.filter((_, i) => i !== index))
  }

  const toggleFetchAll = () => {
    setFetchAll((prev) => {
      const next = !prev
      if (next) {
        setTags([])
        setTagInput('')
      }
      return next
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onContinue({ tags: fetchAll ? [] : tags, fetchAll })
  }

  return (
    <FullScreenModal showModal={showIntegrationModal}>
      <div className='flex justify-center items-center h-[100%] bg-[#2229455a]'>
        <div className='w-[626px] rounded-lg p-8 bg-white'>
          <div className='flex justify-between'>
            <Typography variant='title-regular' className='flex text-2xl items-center font-bold'>
              <TargetIcon
                width={'48px'}
                height={'48px'}
                type={'JIRA'}
                className='rounded'
                style={{ borderRadius: '12px', marginRight: '15px' }}
              />
              Setup Jira Integration
            </Typography>
          </div>

          <form onSubmit={handleSubmit}>
            {!message ? (
              <>
                <div className='p-4 mt-2 rounded text-black overflow-auto'>
                  <TextInput
                    data-testid={getHTMLDataTestID('integration', 'input', 'name')}
                    label='Integration Name'
                    onChange={(e) => onInputChange(e.target.value, 'name')}
                    placeholder='Please enter name'
                    style={{ width: '520px' }}
                  />

                  {/* Project Keys (comma-separated, chips) */}
                  <div className='mt-4'>
                    <Typography variant='body-regular'>Project Keys</Typography>

                    <div
                      className={[
                        'border rounded px-2 py-1 flex flex-wrap items-center min-h-[2.5rem] w-[520px] transition-opacity',
                        fetchAll ? 'opacity-50 pointer-events-none' : ''
                      ].join(' ')}
                    >
                      {tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className='bg-[var(--color-primary-light)] text-[var(--color-primary)] px-2 py-0.5 rounded mr-1 mb-1 flex items-center'
                        >
                          {tag}
                          <button
                            type='button'
                            onClick={() => handleTagDelete(idx)}
                            className='ml-1 hover:opacity-80'
                            aria-label='Remove key'
                          >
                            &times;
                          </button>
                        </span>
                      ))}

                      <input
                        type='text'
                        className='flex-1 min-w-[120px] outline-none py-1'
                        placeholder='Type project keys, press comma and enter'
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={handleTagKeyDown}
                        onBlur={handleTagBlur}
                        disabled={fetchAll}
                      />
                    </div>
                  </div>

                  {/*<div className='flex justify-between my-3 items-center'>
                    <span>Self Hosted</span>
                    <SwitchBox
                      data-testid={getHTMLDataTestID('integration', 'input', 'self-hosted')}
                      checked={isSelfHosted}
                      onChange={() => setIsSelfHosted(!isSelfHosted)}
                      variant='primary'
                      width='60px'
                      style={{ background: 'none' }}
                    />
                  </div> */}

                  {isSelfHosted && (
                    <TextInput
                      data-testid={getHTMLDataTestID('integration', 'input', 'host-url')}
                      label='BaseUrl'
                      onChange={(e) => onInputChange(e.target.value, 'hostUrl')}
                      placeholder='URL'
                      style={{ width: '520px' }}
                    />
                  )}
                </div>

                <div className='flex justify-between px-4 items-center mt-4'>
                  <LabelCheckbox
                    checked={fetchAll}
                    name='Fetch All Project'
                    onClick={toggleFetchAll}
                  />

                  <div>
                    {nameError !== '' ? <Label variant='warning' text={nameError} /> : null}
                  </div>

                  <div className='flex justify-end gap-5'>
                    <Button
                      data-testid={getHTMLDataTestID('integration', 'button', 'cancel')}
                      size='md'
                      onClick={() => setShowIntegrationModal(false)}
                      variant='gray'
                    >
                      Cancel
                    </Button>
                    <Button
                      data-testid={getHTMLDataTestID('integration', 'button', 'continue')}
                      disabled={isDisabled}
                      size='md'
                      type='submit'
                      variant='primary'
                    >
                      Continue
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className='flex items-center bg-[#f2f4fb] p-5 mt-5'>
                <div className='mt-2'>
                  <LoadingSpinner size={20} thickness={4} />
                </div>
                <Label text={message} variant='grayBlue' />
              </div>
            )}
          </form>
        </div>
      </div>
    </FullScreenModal>
  )
}

export { Jira }
